import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Checkbox, IconButton, Stack, Tooltip, Typography,
} from '@mui/material';
import AlphaSCircleOutlineIcon from 'mdi-react/AlphaSCircleOutlineIcon';
import { editorTheme } from '../utils/editor-theme';
import {
  EDITOR_LAYER_KEYS,
  isSoloableLayer,
  type EditorLayerKey,
  type LayerSettings,
} from '../utils/layer-settings';

interface Props {
  readonly settings: LayerSettings;
  readonly solo: EditorLayerKey | null;
  readonly onVisibleChange: (key: EditorLayerKey, visible: boolean) => void;
  /** Omitted for the simple checklist, which offers show and hide and nothing else. */
  readonly onSoloChange?: (key: EditorLayerKey) => void;
  /**
   * Selects everything drawn on a layer.
   *
   * The row's name does it, because that is what "clicking a layer" means everywhere else: the
   * checkbox is a light switch and the S is a solo, and neither of them is what an admin who
   * clicked the word "Desks" was asking for.
   */
  readonly onSelectLayer?: (key: EditorLayerKey) => void;
  /**
   * How many desks the retired-desks row is hiding.
   *
   * Carried on the row because an unexplained gap in a row of desks is the one thing this
   * feature can leave behind that looks like data loss.
   */
  readonly retiredDeskCount: number;
  /**
   * `horizontal` lays the layers out side by side, for the strip along the bottom of the plan;
   * `vertical`, the default, is one per row, for the side panel.
   */
  readonly orientation?: 'vertical' | 'horizontal';
}

const LABEL_KEYS: Readonly<Record<EditorLayerKey, string>> = {
  floor: 'floorplan-editor-layer-floor',
  rooms: 'floorplan-editor-layer-rooms',
  border: 'floorplan-editor-layer-border',
  deskShapes: 'floorplan-editor-layer-desk-shapes',
  desks: 'floorplan-editor-layer-desks',
  labels: 'floorplan-editor-layer-labels',
  retiredDesks: 'floorplan-editor-layer-retired-desks-count',
};

/**
 * Which layers are drawn, and which one is being worked on alone.
 *
 * A view setting throughout: nothing here is saved, and the document keeps every feature it
 * loaded however much of it is hidden. Two controls per row and no more — the opacity slider
 * that used to sit here doubled the height of every row to answer a question solo already
 * answers.
 */
const LayersPanel = ({
  settings,
  solo,
  onVisibleChange,
  onSoloChange,
  onSelectLayer,
  retiredDeskCount,
  orientation = 'vertical',
}: Props): React.JSX.Element => {
  const { t } = useTranslation();
  const horizontal = orientation === 'horizontal';

  return (
    <Stack
      direction={horizontal ? 'row' : 'column'}
      spacing={horizontal ? 0.25 : 0.5}
      sx={{ p: horizontal ? 0 : 1, flexWrap: horizontal ? 'nowrap' : undefined }}
    >
      {EDITOR_LAYER_KEYS.map((key) => {
        // The retired-desks row earns its keep only when there is something behind it.
        if (key === 'retiredDesks' && retiredDeskCount === 0) {
          return null;
        }

        // `total` rather than `count`, so i18next resolves the key as written instead of
        // looking for plural variants the translation files do not carry.
        const label = key === 'retiredDesks'
          ? String(t(LABEL_KEYS[key], { total: retiredDeskCount }))
          : String(t(LABEL_KEYS[key]));

        const soloable = isSoloableLayer(key);
        const soloed = solo === key;
        // Dimmed rather than disabled while another layer is soloed: the boxes still mean
        // what they meant, and they are what the view goes back to when solo is turned off.
        const overridden = solo !== null && !soloed;

        return (
          <Stack
            key={key}
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{
              px: 0.5,
              borderRadius: `${editorTheme.radiusSmall}px`,
              '&:hover': { backgroundColor: editorTheme.surfaceSunken },
            }}
          >
            <Checkbox
              size="small"
              checked={settings[key].visible}
              onChange={(event): void => onVisibleChange(key, event.target.checked)}
              inputProps={{ 'aria-label': label, id: `floorplan-layer-${key}` }}
            />

            {/* Without a select action the name is the checkbox's own label, so clicking the
                word does what anybody new to this expects of it: switches the layer. */}
            {!onSelectLayer && (
              <Typography
                component="label"
                htmlFor={`floorplan-layer-${key}`}
                variant="body2"
                sx={{
                  flexGrow: 1,
                  minWidth: 0,
                  cursor: 'pointer',
                  color: editorTheme.ink,
                  pr: 1,
                  whiteSpace: 'nowrap',
                  fontSize: horizontal ? 13 : undefined,
                }}
              >
                {label}
              </Typography>
            )}

            {onSelectLayer && (
            <Tooltip title={String(t('floorplan-editor-layer-select', { layer: label }))}>
              <Typography
                component="button"
                type="button"
                variant="caption"
                onClick={(): void => onSelectLayer(key)}
                sx={{
                  flexGrow: 1,
                  minWidth: 0,
                  opacity: overridden ? 0.5 : 1,
                  border: 'none',
                  background: 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                  color: 'inherit',
                  p: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  '&:hover': { color: editorTheme.accent },
                  '&:focus-visible': { outline: `2px solid ${editorTheme.accent}`, outlineOffset: 2 },
                }}
              >
                {label}
              </Typography>
            </Tooltip>
            )}

            {soloable && onSoloChange && (
              <Tooltip title={String(t(soloed ? 'floorplan-editor-layer-unsolo' : 'floorplan-editor-layer-solo'))}>
                <IconButton
                  size="small"
                  onClick={(): void => onSoloChange(key)}
                  aria-label={String(t(soloed ? 'floorplan-editor-layer-unsolo' : 'floorplan-editor-layer-solo', { layer: label }))}
                  aria-pressed={soloed}
                  sx={{ color: soloed ? editorTheme.accent : editorTheme.inkFaint }}
                >
                  <AlphaSCircleOutlineIcon size={16} />
                </IconButton>
              </Tooltip>
            )}

          </Stack>
        );
      })}
    </Stack>
  );
};

export default LayersPanel;
